/**
 * Non-redirecting error surfaces for the `<SimplAuthGuard>` terminal
 * statuses that are NOT ``unauthed``:
 *
 *   - ``account-not-provisioned`` (HTTP 404 from whoami)
 *   - ``forbidden``               (HTTP 403 from whoami)
 *   - ``service-unavailable``     (HTTP 5xx / network / timeout / 200
 *                                  with an invalid body)
 *
 * The pre-SE-1765 guard collapsed every non-401 case to ``unauthed``
 * and immediately redirected to `<SignInRedirect>`. That produced a
 * tight loop for any signed-in user whose whoami wasn't 200:
 *
 *   probe → 4xx-not-401 / 5xx / network
 *     → SignInRedirect
 *     → simpl_auth mints a fresh JWT for the same identity
 *     → SPA reloads
 *     → probe → same failure → loop.
 *
 * These surfaces break the loop by refusing to redirect. Each one
 * renders a plain human explanation, an optional developer-diagnostic
 * paragraph, and — critically — a Sign out button, which is the only
 * escape hatch that clears the JWT cookie without shell access.
 *
 * Only ``service-unavailable`` also exposes Retry: it's the only
 * status whose cause (transient 5xx, brief network hiccup) can
 * plausibly resolve without user action. The other two need real
 * intervention (an admin provisioning the account, an admin lifting
 * a block) and offering retry would just recreate the loop at a
 * slower cadence.
 */

import { ReactNode } from "react";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";

import { SignOutButton } from "../../common-ui/SignOutButton";

const CenteredSurface = ({ children }: { children: ReactNode }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 2,
      minHeight: "100vh",
      p: 3
    }}
  >
    <Box sx={{ maxWidth: 560, width: "100%" }}>{children}</Box>
  </Box>
);

const ProbeDiagnostic = ({
  probeStatus
}: {
  probeStatus?: number | null;
}) => {
  // Rendered as small text under the primary message. Not the main
  // signal — the human explanation is — but useful when a support
  // channel says "share what the page says" and we want the HTTP
  // status pasted along with it.
  if (probeStatus === undefined) return null;
  const label =
    probeStatus === null ? "network unreachable" : `HTTP ${probeStatus}`;
  return (
    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
      Diagnostic: whoami probe returned {label}.
    </Typography>
  );
};

type SurfaceProps = {
  probeStatus?: number | null;
};

/**
 * Rendered when whoami returns 404 — the JWT is valid but simpl-api's
 * `user` table has no row for the signed-in email. The old codepath
 * used to show a distinct "user could not be found" page here; the
 * simpl_auth-migration guard lost it by coercing 404 into ``unauthed``.
 */
export const AccountNotProvisionedSurface = ({ probeStatus }: SurfaceProps) => (
  <CenteredSurface>
    <Alert severity="warning" sx={{ mb: 2 }}>
      Your account isn&apos;t provisioned in SIMPL.
    </Alert>
    <Typography variant="body1" sx={{ mb: 1 }}>
      You&apos;re signed in, but there&apos;s no SIMPL user record for your
      email address yet. Signing in again will land you back on this
      page — an administrator needs to add your account before you can
      continue.
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      If you were expecting access, contact your program administrator
      or SIMPL support.
    </Typography>
    <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
      <SignOutButton />
    </Stack>
    <ProbeDiagnostic probeStatus={probeStatus} />
  </CenteredSurface>
);

/**
 * Rendered when whoami returns 403 — JWT valid, caller explicitly
 * rejected. Rare on `/api/user` specifically (most 403s come from
 * downstream authorization), but included so the terminal-status
 * exhaust is complete: any 403 that reaches the guard should be
 * surfaced, not looped.
 */
export const AccessDeniedSurface = ({ probeStatus }: SurfaceProps) => (
  <CenteredSurface>
    <Alert severity="error" sx={{ mb: 2 }}>
      Access denied.
    </Alert>
    <Typography variant="body1" sx={{ mb: 1 }}>
      You&apos;re signed in, but your account isn&apos;t authorized to use
      this application. Signing in again won&apos;t change that — please
      contact your administrator if you believe this is a mistake.
    </Typography>
    <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
      <SignOutButton />
    </Stack>
    <ProbeDiagnostic probeStatus={probeStatus} />
  </CenteredSurface>
);

type ServiceUnavailableProps = SurfaceProps & {
  /**
   * Re-runs the whoami probe. Wired to `useSimplAuthSession`'s
   * ``retry`` handler — pressing this button drives the session back
   * to ``loading`` and re-invokes ``probeSimplAuthSession``. Undefined
   * only in tests / storybook usage; the guard always passes it in.
   */
  onRetry?: () => void;
};

/**
 * Rendered when whoami hits a 5xx, network failure, timeout, or a 200
 * with a structurally invalid body. Cause is server-side or transport,
 * and a fresh sign-in wouldn't touch either. Exposes Retry (which
 * simply re-runs the probe) alongside Sign out.
 */
export const ServiceUnavailableSurface = ({
  probeStatus,
  onRetry
}: ServiceUnavailableProps) => (
  <CenteredSurface>
    <Alert severity="error" sx={{ mb: 2 }}>
      SIMPL is temporarily unavailable.
    </Alert>
    <Typography variant="body1" sx={{ mb: 1 }}>
      We couldn&apos;t verify your session because the API isn&apos;t
      responding right now. This usually clears in a moment. Try again
      in a few seconds — you don&apos;t need to sign in again.
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      If it keeps happening, sign out and contact SIMPL support.
    </Typography>
    <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
      {onRetry && (
        <Button variant="contained" onClick={onRetry}>
          Retry
        </Button>
      )}
      <SignOutButton />
    </Stack>
    <ProbeDiagnostic probeStatus={probeStatus} />
  </CenteredSurface>
);
